import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';
import api from '../services/api';

const ImportPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [readingData, setReadingData] = useState(null);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('No import token provided. Please use a valid import link from your tarot reading.');
      return;
    }

    importReading(token);
  }, [searchParams]);

  const importReading = async (token) => {
    try {
      setStatus('loading');
      setMessage('Importing your tarot reading...');

      const response = await api.importReading(token);
      
      if (response.success) {
        setStatus('success');
        setMessage('Your tarot reading has been successfully imported to your diary!');
        
        // Optionally fetch the imported reading data
        if (response.readingId) {
          try {
            const reading = await api.getReading(response.readingId);
            setReadingData(reading);
          } catch (err) {
            console.warn('Could not fetch reading details:', err);
          }
        }

        // Redirect to diary after a delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        throw new Error(response.message || 'Import failed');
      }
    } catch (error) {
      setStatus('error');
      if (error.response?.status === 401) {
        setMessage('Invalid or expired import token. Please generate a new import link from your tarot reading.');
      } else {
        setMessage(error.message || 'Failed to import reading. Please try again.');
      }
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Clock className="spinner" size={48} />;
      case 'success':
        return <CheckCircle size={48} style={{ color: '#28a745' }} />;
      case 'error':
        return <AlertCircle size={48} style={{ color: '#dc3545' }} />;
      default:
        return null;
    }
  };

  const getStatusClass = () => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return '';
    }
  };

  return (
    <div className="import-page">
      <div className="card">
        <div className="import-status">
          <div className="status-icon">
            {getStatusIcon()}
          </div>
          
          <div className={`status-message ${getStatusClass()}`}>
            <h2>
              {status === 'loading' && 'Importing Reading...'}
              {status === 'success' && 'Import Successful!'}
              {status === 'error' && 'Import Failed'}
            </h2>
            <p>{message}</p>
          </div>

          {readingData && status === 'success' && (
            <div className="reading-preview">
              <h3>Imported Reading:</h3>
              <div className="reading-summary">
                <p><strong>Spread:</strong> {readingData.spreadName}</p>
                <p><strong>Date:</strong> {new Date(readingData.timestamp).toLocaleDateString()}</p>
                <p><strong>Cards:</strong> {readingData.cards.length} cards drawn</p>
              </div>
            </div>
          )}

          <div className="import-actions">
            {status === 'success' && (
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/')}
              >
                View My Diary
              </button>
            )}
            
            {status === 'error' && (
              <div className="error-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => navigate('/')}
                >
                  Go to Diary
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportPage;
